import type { PushDeviceStatus, PushPlatform } from "dutyon-server/types";
import { httpClient } from "./httpClient";

const PUSH_INSTALLATION_STORAGE_KEY = "dutyon.push.installation-id";
let volatileInstallationId: string | null = null;

function createInstallationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  }
  return `installation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getPushInstallationId() {
  if (volatileInstallationId) return volatileInstallationId;
  try {
    const stored = localStorage.getItem(PUSH_INSTALLATION_STORAGE_KEY);
    if (stored) {
      volatileInstallationId = stored;
      return stored;
    }
    const created = createInstallationId();
    localStorage.setItem(PUSH_INSTALLATION_STORAGE_KEY, created);
    volatileInstallationId = created;
    return created;
  } catch {
    volatileInstallationId = createInstallationId();
    return volatileInstallationId;
  }
}

export async function getMyPushDeviceStatus(): Promise<PushDeviceStatus> {
  const response = await httpClient.get<PushDeviceStatus>("/users/me/push-devices", {
    params: { installationId: getPushInstallationId() },
  });
  return response.data;
}

export async function registerMyPushDevice(
  input: {
    token: string;
    platform: PushPlatform;
  },
  sessionToken?: string,
): Promise<PushDeviceStatus> {
  const config = sessionToken
    ? { headers: { Authorization: `Bearer ${sessionToken}` }, _retry: true }
    : undefined;
  const response = await httpClient.post<PushDeviceStatus>(
    "/users/me/push-devices",
    {
      ...input,
      installationId: getPushInstallationId(),
    },
    config,
  );
  return response.data;
}

export async function unregisterMyPushDevice(sessionToken?: string): Promise<PushDeviceStatus> {
  const response = await httpClient.delete<PushDeviceStatus>("/users/me/push-devices", {
    data: { installationId: getPushInstallationId() },
    ...(sessionToken ? { headers: { Authorization: `Bearer ${sessionToken}` }, _retry: true } : {}),
    timeout: 5_000,
  });
  return response.data;
}
